import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Book, Search, Calendar, LogIn, Menu, X, CalendarDays, Plus } from 'lucide-react';
import { format, parseISO, addDays, isValid, startOfDay, isSameDay } from 'date-fns';
import DevotionCard from './components/DevotionCard';
import { SectionNav } from './components/SectionNav';
import { DevotionCalendar } from './components/DevotionCalendar';
import { Devotion, DevotionSection, Event, EventType, EVENT_TYPES } from './types';
import { supabase } from './lib/supabase';
import { Auth } from './components/Auth';
import { useAuth } from './hooks/useAuth';
import { useDevotions } from './hooks/useDevotions';
import { getUserProgress, clearUserData } from './lib/progress';
import { SettingsProvider } from './contexts/SettingsContext';
import { SettingsButton } from './components/SettingsButton';
import { useDevotionsData } from './hooks/useDevotionsData';
import { EventsList } from './components/EventsList';
import { AddEventForm } from './components/AddEventForm';
import { EventCard } from './components/EventCard';
import { useEvents } from './hooks/useEvents';
import 'react-day-picker/dist/style.css';

const sectionColors: Record<DevotionSection, string> = {
  "Faith for Divine Fulfilled Expectations": "#EF4444",
  "Personal Relationship with God": "#8B5CF6",
  "The Abrahamic Way": "#10B981",
  "Church Building and Service": "#F59E0B",
  "Divine Achievements": "#3B82F6"
};

function App() {
  const { session, userProgress, setUserProgress } = useAuth();
  const { devotions, isLoading: isLoadingDevotions } = useDevotionsData();
  const { 
    completedDevotions, 
    toggleDevotion, 
    resetDevotions, 
    isSyncing 
  } = useDevotions(session, userProgress);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentSection, setCurrentSection] = useState<DevotionSection | 'all'>('all');
  const [selectedDate, setSelectedDate] = useState<Date>(startOfDay(new Date()));
  const [isSearching, setIsSearching] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [showSidebar, setShowSidebar] = useState(false);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [expandedDevotionIds, setExpandedDevotionIds] = useState<Set<number>>(new Set());
  const [showEvents, setShowEvents] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [eventsKey, setEventsKey] = useState(0); // Add a key to force re-render of events
  const [selectedEventType, setSelectedEventType] = useState<EventType | 'all'>('all');

  // Get all events when showing events page, or events for selected date when in calendar view
  const { events: allEvents, refreshEvents: refreshAllEvents } = useEvents();
  const { events: selectedDateEvents, refreshEvents: refreshSelectedDateEvents } = useEvents(selectedDate);
  
  // Use the appropriate events list based on the current view
  const events = showEvents ? allEvents : selectedDateEvents;

  // Function to refresh all events
  const refreshAllEventData = useCallback(() => {
    refreshAllEvents();
    refreshSelectedDateEvents();
    setEventsKey(prev => prev + 1);
  }, [refreshAllEvents, refreshSelectedDateEvents]);

  useEffect(() => {
    if (currentSection !== 'all') {
      setViewMode('list');
      // Clear expanded devotions when switching to list view via section
      setExpandedDevotionIds(new Set());
    }
  }, [currentSection]);

  const devotionDates = useMemo(() => {
    const startDate = userProgress?.start_date 
      ? startOfDay(parseISO(userProgress.start_date))
      : startOfDay(new Date());

    const dates = new Map<number, Date>();
    
    devotions.forEach(devotion => {
      const date = addDays(startDate, devotion.day - 1);
      if (!date) {
        console.warn(`Invalid date calculation for devotion ${devotion.id}`);
        return;
      }
      dates.set(devotion.id, date);
    });

    return dates;
  }, [devotions, userProgress?.start_date]);

  // Set selected date and expand today's devotion on first load
  useEffect(() => {
    if (devotions.length === 0) return;
    
    const today = startOfDay(new Date());
    const startDate = userProgress?.start_date 
      ? startOfDay(parseISO(userProgress.start_date))
      : today;

    // Find devotions for today
    const todaysDevotions = devotions.filter(devotion => {
      const devotionDate = devotionDates.get(devotion.id);
      return devotionDate && isSameDay(devotionDate, today);
    });

    if (todaysDevotions.length > 0) {
      // Set today as selected date
      setSelectedDate(today);
      
      // Auto-expand today's devotions
      setExpandedDevotionIds(new Set(todaysDevotions.map(d => d.id)));
    } else {
      // If no devotion for today, use start date
      setSelectedDate(startDate);
      
      // Find devotions for start date
      const startDateDevotions = devotions.filter(devotion => {
        const devotionDate = devotionDates.get(devotion.id);
        return devotionDate && isSameDay(devotionDate, startDate);
      });
      
      if (startDateDevotions.length > 0) {
        setExpandedDevotionIds(new Set(startDateDevotions.map(d => d.id)));
      }
    }
  }, [devotions, devotionDates, userProgress?.start_date]);

  const devotionSections = useMemo(() => {
    return new Map(devotions.map(devotion => [devotion.id, devotion.section]));
  }, [devotions]);

  // Create a map of dates that have events
  const eventDates = useMemo(() => {
    const dates = new Map<string, boolean>();
    allEvents.forEach(event => {
      const dateStr = event.date.split('T')[0]; // Get YYYY-MM-DD part
      dates.set(dateStr, true);
    });
    return dates;
  }, [allEvents]);

  // Group events by type
  const eventsByType = useMemo(() => {
    if (!showEvents) return {};
    
    return events.reduce((acc, event) => {
      const type = event.event_type || 'convention';
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(event);
      return acc;
    }, {} as Record<string, Event[]>);
  }, [events, showEvents]);

  // Filter events by selected type
  const filteredEvents = useMemo(() => {
    if (selectedEventType === 'all') {
      return events;
    }
    return events.filter(event => event.event_type === selectedEventType);
  }, [events, selectedEventType]);

  // Count events by type
  const eventTypeCounts = useMemo(() => {
    const counts: Record<string, number> = { all: events.length };
    
    EVENT_TYPES.forEach(type => {
      counts[type] = events.filter(event => event.event_type === type).length;
    });
    
    return counts;
  }, [events]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setIsSearching(value.length > 0);
    setShowSidebar(false);
    if (value.length > 0) {
      setViewMode('list');
      setExpandedDevotionIds(new Set());
    }
  };

  const handleSectionChange = (section: DevotionSection | 'all') => {
    setCurrentSection(section);
    setSearchTerm('');
    setIsSearching(false);
    setShowSidebar(false);
    setViewMode(section === 'all' ? 'calendar' : 'list');
    setExpandedDevotionIds(new Set());
    setShowEvents(false);
  };

  const handleDateSelect = (date: Date) => {
    if (!date || !isValid(date)) return;
    const normalizedDate = startOfDay(date);
    setSelectedDate(normalizedDate);
    setIsSearching(false);
    setSearchTerm('');
    setShowSidebar(false);
    setViewMode('calendar');
    setCurrentSection('all');
    setShowEvents(false);
    
    // Auto-expand devotions when selected from calendar
    // Find devotions for this date and expand them
    const devotionsForDate = devotions.filter(devotion => {
      const devotionDate = devotionDates.get(devotion.id);
      return devotionDate && isSameDay(devotionDate, normalizedDate);
    });
    
    if (devotionsForDate.length > 0) {
      const newExpandedIds = new Set(devotionsForDate.map(d => d.id));
      setExpandedDevotionIds(newExpandedIds);
    }
  };

  const handleSignOut = async () => {
    try {
      setIsResetting(true);
      await clearUserData();
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Failed to sign out. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  const handleNavigate = (currentDevotion: Devotion, direction: 'prev' | 'next') => {
    const sortedDevotions = [...devotions].sort((a, b) => a.day - b.day);
    const currentIndex = sortedDevotions.findIndex(d => d.id === currentDevotion.id);
    
    if (currentIndex === -1) return;

    const targetIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (targetIndex >= 0 && targetIndex < sortedDevotions.length) {
      const targetDevotion = sortedDevotions[targetIndex];
      const targetDate = devotionDates.get(targetDevotion.id);
      if (targetDate) {
        setSelectedDate(targetDate);
        
        // Auto-expand the newly selected devotion
        setExpandedDevotionIds(new Set([targetDevotion.id]));
      }
    }
  };

  const searchResults = useMemo(() => {
    if (!searchTerm) return [];
    
    const searchLower = searchTerm.toLowerCase();
    return devotions.filter(devotion => 
      devotion.title.toLowerCase().includes(searchLower) ||
      devotion.description.toLowerCase().includes(searchLower) ||
      devotion.theme.toLowerCase().includes(searchLower) ||
      devotion.verse.toLowerCase().includes(searchLower) ||
      devotion.prayer.toLowerCase().includes(searchLower) ||
      devotion.parable.title.toLowerCase().includes(searchLower) ||
      devotion.parable.content.toLowerCase().includes(searchLower) ||
      devotion.reflection.some(item => item.toLowerCase().includes(searchLower)) ||
      devotion.actionSteps.some(step => step.toLowerCase().includes(searchLower))
    );
  }, [devotions, searchTerm]);

  const filteredDevotions = useMemo(() => {
    if (isSearching) {
      return searchResults;
    }

    let filtered = devotions;

    if (currentSection !== 'all') {
      filtered = filtered.filter(devotion => devotion.section === currentSection);
      filtered.sort((a, b) => a.day - b.day);
      return filtered;
    }

    if (viewMode === 'calendar' && selectedDate && isValid(selectedDate)) {
      const normalizedSelectedDate = startOfDay(selectedDate);
      filtered = filtered.filter(devotion => {
        const devotionDate = devotionDates.get(devotion.id);
        if (!devotionDate) return false;
        return isSameDay(devotionDate, normalizedSelectedDate);
      });
    }

    return filtered;
  }, [devotions, currentSection, selectedDate, isSearching, searchResults, devotionDates, viewMode]);

  const devotionCounts = useMemo(() => {
    const counts = devotions.reduce((acc, devotion) => {
      acc[devotion.section] = (acc[devotion.section] || 0) + 1;
      return acc;
    }, {} as Record<DevotionSection | 'all', number>);
    counts.all = devotions.length;
    return counts;
  }, [devotions]);

  const getContentTitle = () => {
    if (isSearching) {
      return `Search Results (${filteredDevotions.length} found)`;
    }
    if (showEvents) {
      if (selectedEventType !== 'all') {
        return `${selectedEventType.charAt(0).toUpperCase() + selectedEventType.slice(1)} Events (${eventTypeCounts[selectedEventType] || 0})`;
      }
      return 'All Events';
    }
    if (viewMode === 'calendar' && selectedDate && isValid(selectedDate)) {
      return `Devotions for ${format(selectedDate, 'MMMM d, yyyy')}`;
    }
    if (currentSection !== 'all') {
      return `${currentSection} (${filteredDevotions.length} devotions)`;
    }
    return 'All Devotions';
  };

  // Get event type color
  const getEventTypeColor = (type: EventType): string => {
    switch (type) {
      case 'convention': return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-300';
      case 'church': return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300';
      case 'home group': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300';
      case 'prayer meeting': return 'bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-300';
      case 'business': return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
      case 'family': return 'bg-rose-100 dark:bg-rose-900 text-rose-800 dark:text-rose-300';
      case 'other': return 'bg-cyan-100 dark:bg-cyan-900 text-cyan-800 dark:text-cyan-300';
      default: return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-300';
    }
  };

  if (showAuth) {
    return <Auth onClose={() => setShowAuth(false)} />;
  }

  return (
    <SettingsProvider>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
        <header className="bg-blue-600 dark:bg-blue-800 text-white shadow-lg fixed top-0 left-0 right-0 z-50 transition-colors">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowSidebar(!showSidebar)}
                  className="md:hidden p-2 hover:bg-blue-700 dark:hover:bg-blue-900 rounded-lg"
                >
                  {showSidebar ? <X size={24} /> : <Menu size={24} />}
                </button>
                <div className="flex items-center space-x-2">
                  <Book size={28} className="hidden sm:block" />
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold">Daily Devotions</h1>
                    <p className="text-blue-100 text-xs sm:text-sm hidden sm:block">
                      A Year of Spiritual Growth
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-4">
                <SettingsButton />
                {session ? (
                  <button
                    onClick={handleSignOut}
                    disabled={isResetting || isSyncing}
                    className={`px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-700 text-white text-sm rounded-lg hover:bg-blue-800 transition-colors ${
                      (isResetting || isSyncing) ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isResetting ? 'Signing Out...' : 'Sign Out'}
                  </button>
                ) : (
                  <button
                    onClick={() => setShowAuth(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <LogIn size={20} />
                    <span className="hidden sm:inline">Sign In</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8 mt-16 md:mt-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className={`
              fixed md:relative inset-0 z-40 bg-gray-50 md:bg-transparent
              transform transition-transform duration-300 ease-in-out
              ${showSidebar ? 'translate-x-0' : '-translate-x-full'}
              md:translate-x-0 md:col-span-3 space-y-6 p-4 md:p-0
              overflow-y-auto dark:bg-gray-900
            `}>
              <SectionNav
                currentSection={currentSection}
                onSectionChange={handleSectionChange}
                devotionCounts={devotionCounts}
              />
              
              <div className="flex space-x-2 mb-4">
                <button
                  onClick={() => {
                    setShowEvents(false);
                    setViewMode('calendar');
                  }}
                  className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                    !showEvents
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <Calendar size={18} />
                  <span>Devotions</span>
                </button>
                <button
                  onClick={() => {
                    setShowEvents(true);
                    setCurrentSection('all');
                    setSelectedEventType('all');
                    refreshAllEventData(); // Refresh events when switching to events view
                  }}
                  className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                    showEvents
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <CalendarDays size={18} />
                  <span>Events</span>
                </button>
              </div>
              
              {showEvents && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-4">
                  <h3 className="font-medium text-gray-800 dark:text-gray-200 mb-3">Event Types</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => setSelectedEventType('all')}
                      className={`w-full text-left px-3 py-2 rounded-lg flex justify-between items-center ${
                        selectedEventType === 'all'
                          ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <span>All Events</span>
                      <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                        {eventTypeCounts.all || 0}
                      </span>
                    </button>
                    
                    {EVENT_TYPES.map(type => (
                      <button
                        key={type}
                        onClick={() => setSelectedEventType(type)}
                        className={`w-full text-left px-3 py-2 rounded-lg flex justify-between items-center ${
                          selectedEventType === type
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${getEventTypeColor(type)}`}>
                          {eventTypeCounts[type] || 0}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <DevotionCalendar
                key={`${resetKey}-${eventsKey}`}
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
                completedDays={completedDevotions}
                devotionDates={devotionDates}
                sectionColors={sectionColors}
                devotionSections={devotionSections}
                eventDates={eventDates}
              />
              
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 transition-colors">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">About Us</h2>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  This mobile app features 200 daily devotions inspired by the 2024-25 Pan African and World Christian Churches Convention. These devotions are designed to guide you through a year of faith, expectation, and transformation, reflecting the central themes of the convention.
                  <br /><br />
                  Drawing upon the rich spiritual insights and messages shared at the convention, this app aims to help you connect with God and deepen your personal relationship with Him. The devotions encourage a move from passive waiting to active, faith-filled anticipation in this 'year of fulfilled divine expectations'.
                  <br /><br />
                  The 200 devotions are thoughtfully structured into five contextual groups: Faith and Expectation, Personal Relationship with God, The Abrahamic Way, Church Building and Service, and Divine Achievements. The content is based on the teachings of Apostle Andrew Wutawunashe and was edited by Overseer Peter Mukwena, with structuring and formatting by Mark Newstead.
                  <br /><br />
                  We pray this app serves as a practical resource to equip you for a life of faith, purpose, and divine impact throughout the year.
                </p>
              </div>
            </div>

            <div className="md:col-span-9">
              <div className="mb-8">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search all devotions..."
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>
              </div>

              {!isSearching && viewMode === 'calendar' && !showEvents && (
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-800">Your Progress</h2>
                    <Calendar size={24} className="text-blue-600" />
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full"
                      style={{ width: `${(completedDevotions.length / devotions.length) * 100}%` }}
                    ></div>
                  </div>
                  <p className="text-gray-600 mt-2">
                    {completedDevotions.length} of {devotions.length} devotions completed
                  </p>
                </div>
              )}

              <div className="mb-6 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                  {getContentTitle()}
                </h2>
                {showEvents && session && (
                  <button
                    onClick={() => setShowAddEvent(true)}
                    className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus size={16} />
                    <span>Add Event</span>
                  </button>
                )}
              </div>

              {showEvents ? (
                <div className="space-y-6">
                  {filteredEvents.length === 0 ? (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 text-center text-gray-500 dark:text-gray-400">
                      {selectedDate && !showEvents ? (
                        `No events found for ${format(selectedDate, 'MMMM d, yyyy')}`
                      ) : (
                        selectedEventType !== 'all' ? 
                        `No ${selectedEventType} events found` : 
                        'No events found'
                      )}
                    </div>
                  ) : (
                    selectedEventType === 'all' ? (
                      // Group events by type when showing all events
                      Object.entries(eventsByType)
                        .sort(([typeA], [typeB]) => {
                          // Custom sort order for event types
                          const order = {
                            'convention': 0,
                            'church': 1,
                            'prayer meeting': 2,
                            'home group': 3,
                            'family': 4,
                            'business': 5,
                            'other': 6
                          };
                          return (order[typeA as EventType] || 99) - (order[typeB as EventType] || 99);
                        })
                        .map(([type, typeEvents]) => (
                          <div key={type} className="mb-6">
                            <h3 className={`text-lg font-semibold mb-3 px-3 py-1 rounded-lg inline-block ${getEventTypeColor(type as EventType)}`}>
                              {type.charAt(0).toUpperCase() + type.slice(1)} Events ({typeEvents.length})
                            </h3>
                            <div className="space-y-4">
                              {typeEvents.map(event => (
                                <EventCard 
                                  key={event.id} 
                                  event={event} 
                                  session={session} 
                                  onDelete={refreshAllEventData}
                                />
                              ))}
                            </div>
                          </div>
                        ))
                    ) : (
                      // Show filtered events without grouping
                      <div className="space-y-4">
                        {filteredEvents.map(event => (
                          <EventCard 
                            key={event.id} 
                            event={event} 
                            session={session} 
                            onDelete={refreshAllEventData}
                          />
                        ))}
                      </div>
                    )
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredDevotions.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-500">
                      {isSearching 
                        ? 'No devotions found matching your search'
                        : 'No devotions found for the selected criteria'
                      }
                    </div>
                  ) : (
                    <>
                      {filteredDevotions.map(devotion => {
                        const sortedDevotions = [...devotions].sort((a, b) => a.day - b.day);
                        const currentIndex = sortedDevotions.findIndex(d => d.id === devotion.id);
                        const hasPrevious = currentIndex > 0;
                        const hasNext = currentIndex < sortedDevotions.length - 1;
                        
                        // Check if this devotion should be expanded
                        const isExpanded = expandedDevotionIds.has(devotion.id);

                        return (
                          <DevotionCard
                            key={`${devotion.id}-${resetKey}`}
                            devotion={{
                              ...devotion,
                              completed: completedDevotions.includes(devotion.id)
                            }}
                            onComplete={toggleDevotion}
                            isSearchResult={viewMode === 'list' || isSearching}
                            session={session}
                            onNavigate={
                              viewMode === 'calendar'
                                ? (direction) => handleNavigate(devotion, direction)
                                : undefined
                            }
                            hasPrevious={hasPrevious}
                            hasNext={hasNext}
                            initialExpanded={isExpanded}
                          />
                        );
                      })}
                      
                      {/* Show events for the selected date if in calendar view */}
                      {viewMode === 'calendar' && selectedDateEvents.length > 0 && (
                        <div className="mt-8">
                          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                            Events for {format(selectedDate, 'MMMM d, yyyy')}
                          </h3>
                          {selectedDateEvents.map(event => (
                            <EventCard 
                              key={event.id} 
                              event={event} 
                              session={session} 
                              onDelete={refreshAllEventData}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </main>

        {showAddEvent && session && (
          <AddEventForm 
            onClose={() => {
              setShowAddEvent(false);
              refreshAllEventData(); // Refresh events after adding a new event
            }} 
            session={session}
            selectedDate={selectedDate}
          />
        )}
      </div>
    </SettingsProvider>
  );
}

export default App;